/**
 * teslaCoilPipeline — a Tesla coil with arcing electricity, glowing
 * toroid, base, sparks, and ambient plasma haze.
 *
 * Composition (back to front):
 *   1. Floor base — radial dark gradient with bright pulsing glow
 *   2. Coil tower — vertical helix winding from base to sphere
 *   3. Plasma haze — soft volumetric cloud around the tower
 *   4. Sparks — drifting embers in the air
 *   5. Arcs — 6 electric arcs from sphere tip to ground points,
 *      each with its own phase + flicker
 *   6. Sphere — bright white-blue ionised bulb at the top
 *
 * Audio reactivity:
 *   bass — drives arc intensity, sphere brightness, base glow
 *   mid  — drives helix speed, secondary glow color shift
 *   high — drives per-arc flicker & spark generation
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TeslaCoilPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float r = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    r += a * noise2(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return r;
}

// Hash for 1D lookup of arc phases.
float h11(float n) {
  return fract(sin(n * 91.345) * 47453.12);
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);
  float total = bass * 0.55 + mid * 0.30 + high * 0.15;

  // Coil origin (centered, lower portion).
  vec2 coil = vec2(0.0, -0.15);

  // --- 1. Background gradient + texture ---
  // Deep blue→indigo night sky.
  float skyT = clamp((p.y + 0.7) / 1.4, 0.0, 1.0);
  vec3 sky = mix(vec3(0.04, 0.06, 0.13), vec3(0.10, 0.05, 0.18), skyT);
  sky += vec3(0.02, 0.03, 0.08) * fbm(p * 4.0 + vec2(uTime * 0.05, 0.0));

  // Floor — radial dark gradient.
  float floorY = -0.6;
  float floorMask = smoothstep(floorY + 0.04, floorY + 0.20, p.y);
  vec3 floorCol = mix(vec3(0.02, 0.02, 0.05), vec3(0.08, 0.05, 0.10), 1.0 - floorMask);
  floorCol += vec3(0.30, 0.18, 0.45) * pow(1.0 - floorMask, 4.0) * (0.4 + bass * 1.2);

  vec3 col = mix(floorCol, sky, smoothstep(floorY, floorY + 0.10, p.y));

  // Ambient haze — soft FBM glow around the coil.
  float dToCoil = length(p - coil);
  float haze = exp(-dToCoil * 2.5) * (0.4 + bass * 0.6);
  col += vec3(0.15, 0.20, 0.55) * haze;

  // --- 2. Coil tower ---
  // Vertical bounds: coil from y=-0.55 (base) to y=0.55 (sphere center).
  float towerBottom = -0.55;
  float towerTop = 0.45;
  float towerHalfW = 0.05 + 0.01 * sin(uTime * 1.2);
  float yInTower = clamp(p.y, towerBottom, towerTop);

  // Helical winding: x position oscillates based on y + time.
  float helixPhase = (yInTower - towerBottom) * 18.0 - uTime * (2.0 + mid * 4.0);
  float helixX = sin(helixPhase) * towerHalfW;

  // Distance to nearest helix winding point.
  float helixY = (p.y - yInTower) * 14.0;
  float helixD = length(vec2(p.x - helixX, helixY * 0.05));
  float helix = smoothstep(0.04, 0.0, helixD);

  // Metallic copper coil color.
  vec3 copper = vec3(0.7, 0.4, 0.15);
  vec3 copperHot = mix(copper, vec3(1.0, 0.8, 0.4), 0.4 + mid * 0.4);
  col += copperHot * helix * (0.6 + bass * 0.6);

  // Cylinder body (diffuse darker core).
  float coreD = abs(p.x) - 0.025;
  float core = smoothstep(0.005, -0.005, coreD) * smoothstep(towerTop, towerBottom, p.y) * smoothstep(towerBottom - 0.02, towerBottom + 0.02, p.y);
  col += vec3(0.20, 0.10, 0.05) * core;

  // --- 3. Base disc ---
  vec2 baseCenter = vec2(0.0, towerBottom);
  float baseD = length(p - baseCenter);
  float baseDisc = smoothstep(0.32, 0.30, baseD) - smoothstep(0.18, 0.16, baseD);
  col += vec3(0.3, 0.2, 0.1) * baseDisc * 0.6;
  // Base glow pulse.
  float baseGlow = exp(-baseD * 6.0);
  col += vec3(0.4, 0.25, 0.6) * baseGlow * (0.4 + bass * 1.8);

  // --- 4. Sparks / drifting embers ---
  for (int i = 0; i < 32; i++) {
    float fi = float(i);
    float seed = h11(fi * 1.3);
    float sparkT = uTime * (0.4 + seed * 0.8);
    vec2 sparkPos = vec2(
      sin(sparkT * (0.7 + seed * 1.5) + seed * 7.0) * (0.7 + seed * 0.3),
      -0.55 + mod(sparkT * (0.3 + seed * 0.5), 1.6) - 0.2
    );
    vec2 sparkJitter = vec2(
      noise2(vec2(fi, sparkT * 4.0)) - 0.5,
      noise2(vec2(fi + 17.0, sparkT * 4.0)) - 0.5
    ) * 0.04;
    float sparkD = length(p - (sparkPos + sparkJitter));
    float spark = exp(-sparkD * 200.0);
    float sparkAge = mod(sparkT * (0.3 + seed * 0.5), 1.6) / 1.6;
    col += vec3(1.0, 0.7, 0.4) * spark * (1.0 - sparkAge) * (0.3 + high * 0.8);
  }

  // --- 5. Electric arcs from sphere tip ---
  // Sphere center at top.
  vec2 sphereCenter = vec2(0.0, towerTop + 0.05);
  vec2 arcOrigin = sphereCenter + vec2(0.0, 0.08);

  // Six arcs at different angles, each animated.
  float arcTotal = 0.0;
  vec3 arcColor = vec3(0.0);
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float ang = fi * 1.0472 + uTime * 0.15 * (0.5 + fi * 0.2);
    float len = 0.85 + 0.25 * sin(uTime * (3.0 + fi * 1.3) + fi);
    float flicker = step(0.3, sin(uTime * (20.0 + fi * 7.0) + fi * 1.7) * 0.5 + 0.5);
    float intensity = bass * 0.8 + mid * 0.4 + flicker * high;

    // Arc endpoint on the ground / base.
    vec2 endPt = vec2(sin(ang) * len, -0.55 + 0.02 * cos(uTime + fi));
    // Sample the arc as a polyline.
    float minArcD = 1000.0;
    for (int s = 0; s < 24; s++) {
      float fs = float(s) / 23.0;
      // Parameter along arc: midpoint pulled out by noise.
      vec2 mid = mix(arcOrigin, endPt, fs);
      float droop = sin(fs * 3.14159) * 0.18;
      mid.y += droop * (1.0 - 0.5 * sin(ang * 2.0));
      // Noise displacement.
      float nx = noise2(vec2(fs * 6.0 + fi * 13.0, uTime * 4.0 + fi)) - 0.5;
      float ny = noise2(vec2(fs * 6.0 + fi * 13.0 + 31.0, uTime * 4.0 + fi)) - 0.5;
      mid += vec2(nx, ny) * 0.05 * fs;
      float d = length(p - mid);
      if (d < minArcD) minArcD = d;
    }
    float arcGlow = exp(-minArcD * 250.0);
    float arcHalo = exp(-minArcD * 25.0) * 0.15;
    arcTotal += arcGlow * intensity;
    arcColor += vec3(0.6, 0.85, 1.0) * (arcGlow + arcHalo) * intensity;
  }

  col += arcColor;

  // --- 6. Sphere (top ionised bulb) ---
  float sphereD = length(p - sphereCenter);
  float sphereR = 0.07 + bass * 0.02;
  float sphereCore = smoothstep(sphereR, 0.0, sphereD);
  float sphereHalo = exp(-sphereD * 8.0) * 0.8;
  vec3 sphereCol = mix(vec3(0.7, 0.85, 1.0), vec3(1.0, 1.0, 1.0), sphereCore);
  col += sphereCol * (sphereCore * (2.5 + bass * 3.0) + sphereHalo);

  // Plasma crackle around sphere.
  float crackle = noise2(p * 30.0 + uTime * 8.0);
  crackle = smoothstep(0.7, 0.95, crackle);
  col += vec3(0.7, 0.8, 1.0) * crackle * sphereHalo * 0.6;

  // --- Floor lightning highlights where arcs strike ---
  float strikeHighlight = 0.0;
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float ang = fi * 1.0472 + uTime * 0.15 * (0.5 + fi * 0.2);
    vec2 endPt = vec2(sin(ang) * (0.85 + 0.25 * sin(uTime * (3.0 + fi * 1.3) + fi)), -0.55);
    float sd = length(p - endPt);
    float shine = exp(-sd * 80.0);
    strikeHighlight += shine * (0.3 + bass * 1.0);
  }
  col += vec3(0.7, 0.9, 1.0) * strikeHighlight * 0.5;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.3;

  // Tone mapping + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeTeslaCoilBands(t: number, out: Float32Array): void {
  // Punchy bass kicks with arc-rebuild rhythm.
  out[0] = 0.45 + 0.45 * Math.abs(Math.sin(t * 1.2));
  out[1] = 0.55 + 0.40 * Math.sin(t * 2.4) * Math.max(0, Math.sin(t * 0.4));
  out[2] = 0.40 + 0.40 * Math.sin(t * 3.1 + 0.7);
  out[3] = 0.50 + 0.40 * Math.sin(t * 1.7 + 1.0);
  out[4] = 0.50 + 0.40 * Math.sin(t * 2.3 + 2.3);
  out[5] = 0.40 + 0.50 * Math.sin(t * 2.9 + 3.1);
  out[6] = 0.30 + 0.60 * Math.sin(t * 5.0 + 0.5) * Math.max(0, Math.sin(t * 0.3));
  out[7] = 0.40 + 0.50 * Math.sin(t * 6.7 + 1.7);
  out[8] = 0.30 + 0.60 * Math.sin(t * 9.0 + 2.1);
}

export function computeTeslaCoilRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.012], [0.012, 0.040], [0.040, 0.080],
    [0.080, 0.180], [0.180, 0.300], [0.300, 0.460],
    [0.460, 0.640], [0.640, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0;
    let count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createTeslaCoilPipeline(gl: WebGL2RenderingContext): TeslaCoilPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    bands: new Float32Array(9),
  };
}

export function drawTeslaCoil(
  gl: WebGL2RenderingContext,
  pipe: TeslaCoilPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}